import React from 'react';
import { useAuth } from '../context/AuthContext';
import { PublicLayout } from '../components/landing/PublicLayout';
import { Card, CardContent } from '../components/ui/card';
import { FileText } from 'lucide-react';

const Section = ({ title, children }) => (
  <div className="mb-8">
    <h2 className="font-cinzel font-bold text-lg text-foreground mb-3 border-b border-border pb-2">
      {title}
    </h2>
    <div className="text-sm text-muted-foreground space-y-2 leading-relaxed">
      {children}
    </div>
  </div>
);

const SubSection = ({ title, children }) => (
  <div className="mt-4">
    <h3 className="font-semibold text-foreground/80 text-sm mb-2">{title}</h3>
    <div className="space-y-2">{children}</div>
  </div>
);

export default function CGUPage() {
  const { language } = useAuth();
  const title = language === 'fr' ? "Conditions Générales d'Utilisation" : 'Terms of Use';
  const subtitle = language === 'fr' ? 'CGU — WyrdMaker' : 'ToU — WyrdMaker';

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="py-16 sm:py-24 px-4 text-center relative">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-background to-background dark:from-primary/8" />
        <div className="relative z-10 max-w-3xl mx-auto">
          <div className="flex justify-center mb-4">
            <FileText className="h-10 w-10 text-primary" />
          </div>
          <h1 className="font-cinzel text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            {title}
          </h1>
          <p className="text-muted-foreground text-base sm:text-lg">{subtitle}</p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-20 sm:pb-28 px-4">
        <div className="max-w-4xl mx-auto">
          <Card className="border-border/50">
            <CardContent className="p-8">

              {/* Préambule */}
              <div className="mb-8 p-4 bg-primary/5 border border-primary/20 rounded-lg text-sm text-muted-foreground leading-relaxed">
                <p>
                  Les présentes Conditions Générales d'Utilisation (ci-après « CGU ») ont pour objet de définir les conditions
                  dans lesquelles <strong>[À COMPLÉTER — dénomination sociale]</strong> (ci-après « l'Éditeur ») met à la disposition des
                  utilisateurs le service <strong>WyrdMaker</strong>, accessible à l'adresse <strong>[À COMPLÉTER — URL du site]</strong>
                  (ci-après « la Plateforme »).
                </p>
                <p className="mt-2">
                  Toute utilisation de la Plateforme implique l'acceptation sans réserve des présentes CGU. L'Éditeur se réserve
                  le droit de modifier ces CGU à tout moment. Les utilisateurs en seront informés par tout moyen utile.
                </p>
              </div>

              <Section title="Article 1 — Définitions">
                <ul className="space-y-2">
                  <li><strong>« Plateforme »</strong> : désigne le service WyrdMaker, ses applications web et ses API.</li>
                  <li><strong>« Éditeur »</strong> : désigne la société ou la personne physique exploitant la Plateforme.</li>
                  <li><strong>« Utilisateur »</strong> : désigne toute personne physique majeure (ou mineure avec autorisation parentale) ou morale qui accède à la Plateforme.</li>
                  <li><strong>« Compte »</strong> : désigne l'espace personnel créé par l'Utilisateur après inscription.</li>
                  <li><strong>« Contenu Utilisateur »</strong> : désigne toutes les données, textes, images, informations et contenus créés ou téléversés par l'Utilisateur sur la Plateforme (campagnes, personnages, cartes, etc.).</li>
                  <li><strong>« Abonnement »</strong> : désigne l'accès payant aux fonctionnalités avancées de la Plateforme selon les plans tarifaires en vigueur.</li>
                  <li><strong>« Joueur invité »</strong> : désigne un utilisateur tiers accédant à une vue partagée via un lien ou un token généré par un Utilisateur inscrit, sans nécessairement disposer d'un compte.</li>
                </ul>
              </Section>

              <Section title="Article 2 — Accès à la Plateforme">
                <SubSection title="2.1 Conditions d'accès">
                  <p>L'accès à la Plateforme est ouvert à toute personne physique majeure ou à toute personne morale. Les mineurs peuvent accéder à la Plateforme sous réserve d'une autorisation préalable et expresse de leur représentant légal.</p>
                  <p>L'utilisation de la Plateforme requiert une connexion internet. L'Éditeur ne saurait être tenu responsable des dysfonctionnements liés à la qualité de cette connexion.</p>
                </SubSection>
                <SubSection title="2.2 Création de compte">
                  <p>L'inscription est obligatoire pour accéder aux fonctionnalités de la Plateforme. L'Utilisateur s'engage à fournir des informations exactes, complètes et à les maintenir à jour.</p>
                  <p>L'Utilisateur est seul responsable de la confidentialité de ses identifiants de connexion. Il s'engage à notifier immédiatement l'Éditeur de toute utilisation non autorisée de son Compte.</p>
                  <p>L'Éditeur se réserve le droit de refuser l'inscription ou de suspendre tout compte en cas de violation des présentes CGU.</p>
                </SubSection>
                <SubSection title="2.3 Disponibilité du service">
                  <p>L'Éditeur s'efforce d'assurer la disponibilité de la Plateforme 24h/24 et 7j/7. Des interruptions pour maintenance peuvent survenir et l'Éditeur en informera les utilisateurs dans la mesure du possible.</p>
                </SubSection>
              </Section>

              <Section title="Article 3 — Description des services">
                <SubSection title="3.1 Offre gratuite">
                  <p>La Plateforme propose un accès gratuit limité comprenant notamment : la création d'un nombre restreint de campagnes, l'accès aux fonctionnalités de base de gestion de personnages, de quêtes et de sessions.</p>
                </SubSection>
                <SubSection title="3.2 Offres payantes">
                  <p>Des abonnements payants (plans « Pro » et « Guilde ») donnent accès à des fonctionnalités avancées : campagnes illimitées, outils de combat avancés, gestion de sons, marketplace de templates, accès multi-joueurs étendu, et autres fonctionnalités décrites sur la page Tarifs.</p>
                  <p>Les conditions financières des abonnements sont régies par les Conditions Générales de Vente (CGV).</p>
                </SubSection>
                <SubSection title="3.3 Accès Joueur (vue partagée)">
                  <p>Certaines fonctionnalités permettent à l'Utilisateur de partager une vue limitée avec des tiers (joueurs) via un lien ou un token temporaire, sans que ces tiers n'aient nécessairement de compte. L'Utilisateur est seul responsable des partages qu'il effectue.</p>
                </SubSection>
              </Section>

              <Section title="Article 4 — Obligations et responsabilités de l'Utilisateur">
                <SubSection title="4.1 Comportement général">
                  <p>L'Utilisateur s'engage à utiliser la Plateforme de manière conforme à sa destination, aux lois et règlements en vigueur, et à ne pas porter atteinte aux droits des tiers ou à l'ordre public.</p>
                </SubSection>
                <SubSection title="4.2 Contenus interdits">
                  <p>Il est formellement interdit de publier, partager ou stocker sur la Plateforme tout contenu :</p>
                  <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                    <li>Illicite, diffamatoire, haineux, pornographique, ou portant atteinte à la dignité humaine</li>
                    <li>Contrefaisant les droits de propriété intellectuelle de tiers</li>
                    <li>Contenant des virus, malwares ou tout code informatique nuisible</li>
                    <li>À caractère publicitaire ou promotionnel non autorisé (spam)</li>
                    <li>Portant atteinte à la vie privée ou aux données personnelles d'autrui</li>
                  </ul>
                </SubSection>
                <SubSection title="4.3 Interdictions techniques">
                  <p>L'Utilisateur s'interdit notamment de :</p>
                  <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                    <li>Tenter de contourner les mécanismes de sécurité de la Plateforme</li>
                    <li>Procéder à du scraping, crawling ou accès automatisé non autorisé à l'API</li>
                    <li>Usurper l'identité d'un autre utilisateur</li>
                    <li>Partager ses identifiants de connexion avec des tiers</li>
                    <li>Utiliser la Plateforme à des fins de revente ou de sous-licence non autorisée</li>
                  </ul>
                </SubSection>
              </Section>

              <Section title="Article 5 — Contenu Utilisateur et propriété intellectuelle">
                <SubSection title="5.1 Propriété des contenus utilisateur">
                  <p>L'Utilisateur demeure propriétaire de ses Contenus Utilisateur. En les publiant sur la Plateforme, il concède à l'Éditeur une licence non exclusive, mondiale, gratuite, pour la durée de son abonnement ou de son utilisation, aux fins d'hébergement, d'affichage et de sauvegarde de ces contenus.</p>
                </SubSection>
                <SubSection title="5.2 Propriété de la Plateforme">
                  <p>La Plateforme, son code source, son design, ses fonctionnalités, ses bases de données et tous les éléments qui la composent sont la propriété exclusive de l'Éditeur et sont protégés par le droit de la propriété intellectuelle. Toute reproduction ou exploitation non autorisée est formellement interdite.</p>
                </SubSection>
                <SubSection title="5.3 Contenus tiers">
                  <p>Les contenus issus de systèmes de jeu de rôle tiers (noms de sorts, règles, etc.) restent la propriété de leurs auteurs respectifs. L'Utilisateur est seul responsable du respect des droits de propriété intellectuelle applicables aux contenus qu'il crée.</p>
                </SubSection>
              </Section>

              <Section title="Article 6 — Protection des données personnelles">
                <p>
                  L'Éditeur traite les données personnelles des Utilisateurs conformément au Règlement Général sur la Protection des Données (RGPD — UE 2016/679) et à la loi Informatique et Libertés.
                </p>
                <p>
                  Les données collectées comprennent notamment : adresse e-mail, nom d'utilisateur, données de connexion, données d'utilisation du service, données de facturation (traitées par les prestataires de paiement Stripe et/ou PayPal).
                </p>
                <p>
                  Les données ne sont pas revendues à des tiers. Elles peuvent être partagées avec des sous-traitants strictement nécessaires à la fourniture du service (hébergeur, processeur de paiement).
                </p>
                <p>
                  Pour toute demande relative à vos données personnelles, consultez nos Mentions Légales ou contactez-nous à <strong>[À COMPLÉTER — email DPO]</strong>.
                </p>
              </Section>

              <Section title="Article 7 — Durée, résiliation et suppression du compte">
                <SubSection title="7.1 Durée">
                  <p>Les présentes CGU sont conclues pour une durée indéterminée à compter de l'inscription de l'Utilisateur sur la Plateforme.</p>
                </SubSection>
                <SubSection title="7.2 Résiliation par l'Utilisateur">
                  <p>L'Utilisateur peut supprimer son Compte à tout moment via les paramètres de son espace personnel ou en contactant l'Éditeur. La suppression du compte entraîne la perte définitive de l'ensemble des Contenus Utilisateur, sauf exportation préalable par l'Utilisateur.</p>
                </SubSection>
                <SubSection title="7.3 Résiliation par l'Éditeur">
                  <p>L'Éditeur se réserve le droit de suspendre ou de résilier l'accès d'un Utilisateur, sans préavis ni indemnité, en cas de violation grave des présentes CGU, d'atteinte aux droits de tiers, ou de comportement frauduleux. En cas d'abonnement payant, les conditions de remboursement décrites dans les CGV s'appliquent.</p>
                </SubSection>
              </Section>

              <Section title="Article 8 — Responsabilité de l'Éditeur">
                <p>
                  L'Éditeur ne saurait être tenu responsable des dommages directs ou indirects résultant de l'utilisation ou de l'impossibilité d'utilisation de la Plateforme,
                  de la perte de données, ou d'une interruption temporaire du service.
                </p>
                <p>
                  La Plateforme est fournie « en l'état ». L'Éditeur ne garantit pas que le service sera exempt d'erreurs ou d'interruptions. Dans les limites autorisées par la loi,
                  la responsabilité totale de l'Éditeur envers un Utilisateur ne saurait excéder le montant payé par cet Utilisateur au cours des 12 derniers mois précédant le sinistre.
                </p>
              </Section>

              <Section title="Article 9 — Modifications des CGU">
                <p>
                  L'Éditeur se réserve le droit de modifier les présentes CGU à tout moment. Les Utilisateurs seront informés de toute modification substantielle par e-mail ou par notification sur la Plateforme au moins <strong>15 jours</strong> avant leur entrée en vigueur.
                </p>
                <p>
                  La poursuite de l'utilisation de la Plateforme après cette période vaut acceptation des CGU modifiées. En cas de refus, l'Utilisateur peut résilier son compte.
                </p>
              </Section>

              <Section title="Article 10 — Droit applicable et règlement des litiges">
                <p>
                  Les présentes CGU sont soumises au droit français. En cas de litige, les parties s'efforceront de trouver une solution amiable. À défaut, les tribunaux compétents du ressort du siège social de l'Éditeur seront seuls compétents.
                </p>
                <p>
                  Conformément aux dispositions du Code de la consommation, tout consommateur peut recourir à un médiateur de la consommation en vue de la résolution amiable du litige.
                  La plateforme européenne de résolution des litiges en ligne est accessible à l'adresse : <strong>https://ec.europa.eu/consumers/odr</strong>.
                </p>
              </Section>

              <p className="text-xs text-muted-foreground/60 mt-8 pt-4 border-t border-border">
                Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>

            </CardContent>
          </Card>
        </div>
      </section>
    </PublicLayout>
  );
}
